import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AssetCard,
  Button,
  Card,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  LabelPill,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute, useParams } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense, useState } from "react";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { graphql } from "~/gql/client";
import { currencyValues } from "~/gql/client/enumValues";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/currencycloud_/$accountId")({
  head: () => opsHead("Currencycloud Account"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { accountId } = useParams({ strict: false });
  if (!accountId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminCurrencycloudAccount($id: ID!) {
        currencycloudAccount(id: $id) {
          ...AdminCurrencycloudAccountDetails
        }
      }

      fragment AdminCurrencycloudAccountDetails on CurrencycloudAccount {
        id
        accountName
        status
        country
        legalEntityType
        spreadTable
        settlementType
        shortReference
        apiTrading
        onlineTrading
        bankAccountVerified
        createdAt
        updatedAt
        balances {
          id
          currency
          amount
          asset {
            id
            ...AssetCardFields
          }
          createdAt
          updatedAt
        }
        contacts {
          id
          createdAt
          updatedAt
          firstName
          lastName
          enabled
          loginId
        }
      }
    `),
    {
      variables: {
        id: accountId,
      },
    },
  );

  const [refreshCurrencycloudAccount] = useMutation(
    graphql(`mutation RefreshCurrencycloudAccount($input: RefreshCurrencycloudAccountInput!) {
      refreshCurrencycloudAccount(input: $input) {
        currencycloudAccount {
          id
        }
      }
    }`),
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-row justify-between gap-4">
        <div className="font-semibold text-xl">Currencycloud Account</div>
        <div className="flex gap-4">
          <Button
            onClick={() =>
              refreshCurrencycloudAccount({
                variables: {
                  input: {
                    currencycloudAccountId: accountId,
                  },
                },
              })
            }
          >
            Sync
          </Button>

          <EmulateInboundFundsDialog currencycloudAccountId={accountId} />
        </div>
      </div>
      <Card className="p-4">
        <DetailRow
          label="ID"
          value={
            <span className="font-mono">{data.currencycloudAccount?.id}</span>
          }
        />
        <DetailRow
          label="Account name"
          value={data.currencycloudAccount?.accountName}
        />
        <DetailRow
          label="Short reference"
          value={data.currencycloudAccount?.shortReference}
        />
        <DetailRow label="Country" value={data.currencycloudAccount?.country} />
        <DetailRow label="Status" value={data.currencycloudAccount?.status} />
        <DetailRow
          label="Legal entity type"
          value={data.currencycloudAccount?.legalEntityType}
        />
        <DetailRow
          label="Spread table"
          value={data.currencycloudAccount?.spreadTable}
        />
        <DetailRow
          label="Settlement type"
          value={data.currencycloudAccount?.settlementType}
        />
        <DetailRow
          label="Bank account verified"
          value={data.currencycloudAccount?.bankAccountVerified}
        />
        <DetailRow
          label="Trading status"
          value={
            <div className="flex gap-2">
              <LabelPill
                label="API trading"
                variant={
                  data.currencycloudAccount?.apiTrading ? "success" : "error"
                }
              />
              <LabelPill
                label="Online trading"
                variant={
                  data.currencycloudAccount?.onlineTrading ? "success" : "error"
                }
              />
            </div>
          }
        />
        <DetailRow
          label="Created"
          value={
            data.currencycloudAccount?.createdAt ? (
              <DateUI
                date={data.currencycloudAccount?.createdAt}
                variant="DATETIME_TZ"
              />
            ) : (
              "-"
            )
          }
        />
        <DetailRow
          label="Last Updated"
          value={
            data.currencycloudAccount?.updatedAt ? (
              <DateUI
                date={data.currencycloudAccount?.updatedAt}
                variant="DATETIME_TZ"
              />
            ) : (
              "-"
            )
          }
        />
      </Card>

      <div className="font-semibold text-xl">Balances</div>

      <DataTable
        data={data.currencycloudAccount?.balances ?? []}
        columns={[
          {
            accessorKey: "amount",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Amount" />
            ),
            cell: ({ row }) => (
              <CurrencyAmountUI
                amount={row.original.amount}
                currency={row.original.currency}
                showFlag
              />
            ),
          },
          {
            accessorKey: "asset",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Asset" />
            ),
            cell: ({ row }) => {
              if (row.original.asset) {
                return (
                  <AssetCard asset={row.original.asset} subText="PROVIDER" />
                );
              }
              return "Not linked";
            },
          },
          {
            accessorKey: "createdAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Created" />
            ),
            cell: ({ row }) => (
              <DateUI date={row.original.createdAt} variant="DATETIME_TZ" />
            ),
          },
          {
            accessorKey: "updatedAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Last updated" />
            ),
            cell: ({ row }) => (
              <DateUI date={row.original.updatedAt} variant="DATETIME_TZ" />
            ),
          },
        ]}
        className="overflow-hidden border"
        hidePagination={true}
        pageSize={10_000}
      />

      <div className="font-semibold text-xl">Contacts</div>

      <DataTable
        data={data.currencycloudAccount?.contacts ?? []}
        columns={[
          {
            accessorKey: "id",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="ID" />
            ),
            cell: ({ row }) => (
              <span className="font-mono">{row.original.id}</span>
            ),
          },
          {
            id: "name",
            accessorFn: (row) => `${row.firstName} ${row.lastName}`,
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Name" />
            ),
          },
          {
            accessorKey: "enabled",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Enabled" />
            ),
          },
          {
            accessorKey: "loginId",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Login ID" />
            ),
          },
          {
            accessorKey: "createdAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Created" />
            ),
            cell: ({ row }) => <DateUI date={row.original.createdAt} />,
          },
          {
            accessorKey: "updatedAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Last updated" />
            ),
            cell: ({ row }) => <DateUI date={row.original.updatedAt} />,
          },
        ]}
        className="overflow-hidden border"
        hidePagination={true}
        pageSize={10_000}
      />
    </div>
  );
}

function EmulateInboundFundsDialog({
  currencycloudAccountId,
}: {
  currencycloudAccountId: string;
}) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<string>("");
  const [reference, setReference] = useState<string>("");

  const [emulateInboundFunds, { loading: isSubmitting }] = useMutation(
    graphql(`mutation EmulateInboundFunds($input: CurrencycloudEmulateInboundFundsInput!) {
      currencycloudEmulateInboundFunds(input: $input) {
        currencycloudAccount {
          id
        }
      }
    }`),
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!amount || !currency || isSubmitting) {
      return;
    }

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      return;
    }

    try {
      await emulateInboundFunds({
        variables: {
          input: {
            currencycloudAccountId,
            amount: new Decimal(numericAmount),
            currency: currency as any,
            reference: reference !== "" ? reference : undefined,
          },
        },
      });

      setOpen(false);
      setAmount("");
      setCurrency("");
      toast({
        kind: "success",
        autoHide: true,
        title: "Inbound funds emulated successfully",
      });
    } catch (error) {
      toast({
        kind: "error",
        autoHide: false,
        title: "Failed to emulate inbound funds",
        description: `Error: ${error}`,
      });
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setAmount("");
      setCurrency("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>Emulate inbound funds</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Emulate Inbound Funds</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            <Select value={currency} onValueChange={setCurrency} required>
              <SelectTrigger>
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {currencyValues.map((curr) => (
                  <SelectItem key={curr} value={curr}>
                    {curr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">Reference</Label>
            <Input
              type="text"
              name="reference"
              placeholder=""
              onChange={(event) => {
                setReference(event.target.value);
              }}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary" disabled={isSubmitting}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="primary"
              type="submit"
              loading={isSubmitting}
              disabled={!amount || !currency || isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Emulate Funds"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
